/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */

import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';
import { batch, useDispatch, useSelector } from 'react-redux';
import React, { useState, useContext } from 'react';
import getCurrencySymbol from 'currency-symbol-map';
import PropTypes from 'prop-types';

import { FormPaymentFieldIndicator, PayButton, UiIndicator } from '../../common-field-styles';
import PaymentModalButton from '../../common-field-components/payment-modal-button';
import { confirmPayment, setUserInputOnCtrl } from '../../../form/reducer/form';
import PaymentForm from '../../../form/components/partials/payment-form';
import { onPaymentValueChange } from '../../../form/slice-actions/form';
import { FormContext } from '../../../shared/contexts/form-context';
import { getLabel } from '../../../../shared/form-lib';
import { ControlPropType } from '../../ctrl_proptype';
import { fplsFetch } from '../../../../shared/utils';
import { logToServer } from '../../../form/utils';

const FlutterwaveSubComponent = ({ ctrl, formDesign, isRenderedOnModal }) => {
  const { inForm, currentPageNo } = useContext(FormContext);

  const {
    editableForm: { flutterwave_public_key, user_custom_texts, respondent_email },
    isPayOnSubmission,
    userAmount,
    payments,
    form_id
  } = useSelector((state) => state.form);

  const submitButtonText = inForm && (formDesign.old.submission_button_text || getLabel('submit_text', user_custom_texts) || 'Submit');
  const { isPaid, isError, message } = payments[ctrl.type][ctrl.internal_id] || {};
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useDispatch();

  const [paymentDetails, setPaymentDetails] = useState({
    amount: ctrl.props.amount || '',
    email: respondent_email || '',
    phone: ''
  });

  const { email, amount, phone } = paymentDetails;

  const finalAmount = Number(userAmount[ctrl.internal_id] ?? (ctrl.props.user_specify ? amount : ctrl.props.amount));
  
  const handleFlutterPayment = useFlutterwave({
    payment_options: 'card,mobilemoney,ussd',
    public_key: flutterwave_public_key,
    tx_ref: `formplus-${Math.random()}`,
    currency: ctrl.props.currency,
    amount: finalAmount,
    customizations: {
      custom_description: ctrl.props.popup_description,
      pay_button_text: ctrl.props.pay_button_text,
      custom_logo: ctrl.props.payment_logo,
      custom_title: ctrl.props.popup_title
    },
    customer: {
      phone_number: phone,
      email
    }
  });

  const handleStorePaymentValue = (status, ref, displayMsg) => {
    const newValue = `Amount Paid: ${finalAmount} ${ctrl.props.currency}\nTransaction ID: ${ref}\n`;

    batch(() => {
      if (status === 'paid') {
        dispatch(
          setUserInputOnCtrl({
            ctrlId: ctrl.internal_id,
            page: currentPageNo - 1,
            property: 'value',
            isValid: true,
            newValue
          })
        );

        if (ctrl.value !== newValue) {
          dispatch(onPaymentValueChange());
        }
      }

      dispatch(
        confirmPayment({
          isError: status === 'error',
          isPaid: status === 'paid',
          ctrlId: ctrl.internal_id,
          message: displayMsg,
          transactionId: ref,
          type: ctrl.type
        })
      );
    });
  };

  const onSuccess = async (response) => {
    // When banktransfer payment options is used the status returns as `completed` and not `successful`.
    const flutterWaveInitSuccess = ['completed', 'successful'].includes(response.status);
    const transactionReference = response.tx_ref; // OR response.transaction_id
    const genericErrorMsg = 'An error occured with your payment.';
    let payload = null;

    const createLogPayload = (status, reason) => {
      const initialPayload = {
        currency: ctrl.props.currency || response.currency,
        amount: finalAmount || response.amount,
        transaction_id: transactionReference,
        gateway: 'Flutterwave',
        form_id
      };

      return {
        ...initialPayload,
        status,
        reason
      };
    };

    // verfiy no foul play
    if (!flutterWaveInitSuccess) {
      handleStorePaymentValue('error', '', genericErrorMsg);
      payload = createLogPayload('error', genericErrorMsg);
    } else {
      try {
        const res = await fplsFetch({
          url: `/api/v2/public/forms/${form_id}/flutterwave`,
          method: 'POST',
          data: {
            transaction_reference: transactionReference,
            amountToCharge: finalAmount,
            field_id: ctrl.internal_id,
            form_id
          }
        });

        if (res.status === 'success') {
          const displayMsg = `Payment of ${finalAmount} ${ctrl.props.currency} approved. Kindly click on “${submitButtonText}” at the bottom of the form to complete transaction`;
          handleStorePaymentValue('paid', transactionReference, displayMsg);
        } else {
          handleStorePaymentValue('error', '', res.message);
        }
        
        payload = createLogPayload(res.status, res.message);
      } catch (error) {
        handleStorePaymentValue('error', '', error.message || genericErrorMsg);
        payload = createLogPayload('error', error.message || genericErrorMsg);
      }
    }

    logToServer(payload);
    closePaymentModal();
  };

  const handleChange = (name, value) => {
    setPaymentDetails((prev) => ({ ...prev, [name]: value }));
  };

  const startPayment = (e) => {
    e.preventDefault();

    if (!flutterwave_public_key) return;

    setOpenModal(false);
    
    handleFlutterPayment({
      callback: (response) => onSuccess(response),
      onClose: () => {}
    });
  };

  return (
    <>
      {isRenderedOnModal ? (
        <PaymentModalButton
          errorMessage={isError ? message || 'An error occured while paying.' : ''}
          onClick={() => inForm && flutterwave_public_key && setOpenModal(true)}
          isTestMode={false}
          isNoKeyAvailable={inForm && !flutterwave_public_key}
          isCompleted={!!(isPaid && ctrl.value)}
          amount={finalAmount}
          ctrl={ctrl}
        />
      ) : (
        <>
          {isPayOnSubmission ? (
            <FormPaymentFieldIndicator>
              There is a {ctrl.props.label} payment field attached to this form. You may be required to make payment of{' '}
              {`${getCurrencySymbol(ctrl.props.currency || 'USD')}${finalAmount}`} after submission.
            </FormPaymentFieldIndicator>
          ) : (
            <>
              {inForm && !flutterwave_public_key && (
                <UiIndicator className="status red">
                  <span>
                    The form owner has not set up their payment integration. Please contact the owner for more
                    information.
                  </span>
                </UiIndicator>
              )}

              {/* Messsage shown when error is returned from calling the payment APIs */}
              {isError && (
                <UiIndicator className="status red">
                  <span>{message || 'An error occured while paying.'}</span>
                </UiIndicator>
              )}

              {!isPaid && !ctrl.value && (
                <PayButton
                  type="button"
                  id={`fpl-${ctrl.internal_id}`}
                  bgColor="#eaa848"
                  onClick={() => inForm && flutterwave_public_key && setOpenModal(true)}>
                  {ctrl.props.pay_button_text}
                </PayButton>
              )}

              {(isPaid || ctrl.value) && (
                <UiIndicator className="paid">
                  <i className="check icon" />
                  <span>{message || ctrl.value}</span>
                </UiIndicator>
              )}
            </>
          )}
        </>
      )}

      {inForm && (
        <PaymentForm
          closeModal={() => setOpenModal(false)}
          handleOpenModal={() => setOpenModal(true)}
          openModal={openModal}
          gateway="flutterwave"
          ctrl={ctrl}
          startPayment={startPayment}
          handleChange={handleChange}
          paymentDetails={paymentDetails}
          message={message}
          finalAmount={finalAmount}
        />
      )}
    </>
  );
}

FlutterwaveSubComponent.defaultProps = {
  isRenderedOnModal: false,
  formDesign: null,
};

FlutterwaveSubComponent.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  isRenderedOnModal: PropTypes.bool,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};

export default FlutterwaveSubComponent;